// SETTINGS V2 — one guest-pass row (split out of GuestPassListV2 for the 300-line
// rule when DEMO_USER_TYPE_SPEC F2 landed).
//
// Renders guest AND demo passes. Demo deltas (spec F2 + security review):
//  • a DEMO pill beside the status — the row must not read like an ordinary
//    machine-scoped guest pass when it actually carries Worker privileges;
//  • the validity window is emphasised and an Active row spells out the time
//    remaining, because expiry — not revoke — is the control that truly ends a
//    demo session;
//  • Resend is hidden (see the comment in GuestPassRowV2Menu).
//
// GP-MENU §7.2: the inline text buttons were replaced by the kebab
// (GuestPassRowV2Menu) under the SAME `canWrite && isActive` condition —
// terminal rows still expose NO actions (nothing in the menu applies to them,
// and the server backs that with 1206).
//
// Inline styles + scoped CSS-var tokens; no MUI/sx/dt/Tailwind.
import React from "react";
import dayjs from "dayjs";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { GuestPass, GuestPassSource } from "../../models/guestPassModels";
import { formatRemaining, isClosingSoon } from "../demoPassUtils";
import GuestPassRowV2Menu from "./GuestPassRowV2Menu";

type StatusStyle = { bg: string; text: string; border: string; labelKey: string };

const STATUS_STYLES: Record<string, StatusStyle> = {
  Active: { bg: "#EAFAF1", text: "#1E7D45", border: "#ABEBC6", labelKey: "guestPass.statusActive" },
  Expired: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB", labelKey: "guestPass.statusExpired" },
  Revoked: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1", labelKey: "guestPass.statusRevoked" },
};

export interface GuestPassRowV2Props {
  pass: GuestPass;
  canWrite: boolean;
  onResend: (pass: GuestPass) => void;
  onRegeneratePin: (pass: GuestPass) => void;
  onRevoke: (pass: GuestPass) => void;
}

const GuestPassRowV2: React.FC<GuestPassRowV2Props> = ({
  pass,
  canWrite,
  onResend,
  onRegeneratePin,
  onRevoke,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();

  const isActive = pass.status === "Active";
  const isDemo = pass.source === GuestPassSource.Demo;
  const remaining = isActive ? formatRemaining(pass.expiresAt) : "";
  const soon = isActive && isClosingSoon(pass.expiresAt);
  const status = STATUS_STYLES[pass.status] || STATUS_STYLES.Expired;

  // Rendered top-right of the phone card (next to the name, TeamRowV2's phone
  // treatment) and at the end of the desktop row.
  const menu =
    canWrite && isActive ? (
      <GuestPassRowV2Menu
        pass={pass}
        onResend={onResend}
        onRegeneratePin={onRegeneratePin}
        onRevoke={onRevoke}
      />
    ) : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isPhone ? "column" : "row",
        alignItems: isPhone ? "stretch" : "center",
        gap: isPhone ? 6 : 12,
        padding: isPhone ? "12px 13px" : "10px 16px",
        borderTop: "1px solid var(--border-soft)",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Guest — on phone this line also carries the kebab on the right, so the
          actions sit at the top of the card instead of below the meta lines. */}
      <div
        style={{
          flex: isPhone ? "unset" : "1 1 200px",
          minWidth: 0,
          display: isPhone ? "flex" : undefined,
          alignItems: isPhone ? "flex-start" : undefined,
          gap: isPhone ? 8 : undefined,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: "var(--ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pass.guestName || "—"}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11.5,
              color: "var(--muted)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pass.guestEmail}
          </div>
        </div>
        {isPhone && menu}
      </div>

      {/* Machine */}
      <div
        style={{
          flex: isPhone ? "unset" : "0 0 130px",
          minWidth: 0,
          fontFamily: "var(--font-mono)",
          fontSize: 12.5,
          color: "var(--ink)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {isPhone && (
          <span style={{ color: "var(--faint)", fontFamily: "inherit" }}>
            {(t("guestPass.colMachine") as string) + ": "}
          </span>
        )}
        {pass.machineInternalNumber}
      </div>

      {/* Window — demo rows read heavier, Active rows spell out what is left. */}
      <div style={{ flex: isPhone ? "unset" : "0 0 220px", minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: isDemo ? 12 : 11.5,
            fontWeight: isDemo ? 600 : 400,
            color: isDemo ? "var(--ink)" : "var(--muted)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {dayjs(pass.validFrom).format("DD.MM.YYYY HH:mm")} →{" "}
          {dayjs(pass.expiresAt).format("DD.MM.YYYY HH:mm")}
        </div>
        {isActive && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              color: soon ? "var(--amber)" : "var(--faint)",
              whiteSpace: "nowrap",
              marginTop: 1,
            }}
          >
            {
              (remaining
                ? t("guestPass.endsIn", { remaining })
                : t("guestPass.endsNow")) as string
            }
          </div>
        )}
      </div>

      {/* Redeems */}
      <div
        style={{
          flex: isPhone ? "unset" : "0 0 70px",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: "var(--muted)",
          whiteSpace: "nowrap",
        }}
      >
        {isPhone && (
          <span style={{ color: "var(--faint)" }}>
            {(t("guestPass.colRedeems") as string) + ": "}
          </span>
        )}
        {pass.redeemCount}
      </div>

      {/* Status (+ DEMO marker) */}
      <div
        style={{
          flex: isPhone ? "unset" : "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 9px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            background: status.bg,
            color: status.text,
            border: `1px solid ${status.border}`,
            whiteSpace: "nowrap",
          }}
        >
          {t(status.labelKey) as string}
        </span>
        {isDemo && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 8px",
              borderRadius: 999,
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              background: "var(--mod-accent-soft)",
              color: "var(--mod-accent-deep)",
              border: "1px solid var(--mod-accent-line)",
              whiteSpace: "nowrap",
            }}
            title={t("guestPass.sourceDemoHint") as string}
          >
            {t("guestPass.sourceDemo") as string}
          </span>
        )}
      </div>

      {/* Actions (desktop/tablet — phone renders the kebab in the name line).
          Omitted entirely when there is no menu, so a terminal row's trailing
          spacing is byte-identical to what shipped before the kebab. */}
      {!isPhone && menu && (
        <div style={{ display: "flex", flexShrink: 0, marginLeft: "auto" }}>{menu}</div>
      )}
    </div>
  );
};

export default GuestPassRowV2;
